/**
 * Generic hook for court search page logic
 * Extracts common state management, effects, and handlers used across all search components
 */

import { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';

import { scrollToElement, updateUrlParams } from '../../../utils';
import { type AppliedFilter, type ViewType } from '../index';
import { getSearchTypeFromQuery, useQueryParams } from '../utils/searchUtils';

interface UseCourtSearchPageConfig<TSearchType extends string> {
  queryPrefixMap: Record<string, TSearchType>;
  defaultSearchType: TSearchType;
  documentTitle: string;
  scrollElementId: string;
  searchQuery: string;
  exactMatch: boolean;
  sortOption: string;
  setSortOption: (value: string) => void;
  triggerCombinedSearch: (
    query: string,
    filters: Record<string, unknown>
  ) => void;
  currentSearchQuery: string;
  setCurrentSearchQuery: (query: string) => void;
  hasResults: boolean;
  isSearching: boolean;
  /** When false, sort changes only update UI sort state (no triggerCombinedSearch). */
  refetchOnSortChange?: boolean;
}

interface UseCourtSearchPageReturn<
  TSearchType extends string,
  TFormData extends {
    searchQuery: string;
    exactMatchOnly: boolean;
    filters: unknown;
  }
> {
  currentSearchQuery: string;
  exactMatch: boolean;
  currentSearchType: TSearchType;
  shouldScrollAfterLoading: boolean;
  currentView: ViewType;
  appliedFilters: AppliedFilter[];
  setCurrentSearchQuery: (query: string) => void;
  setExactMatch: (match: boolean) => void;
  setCurrentSearchType: (type: TSearchType) => void;
  setShouldScrollAfterLoading: (scroll: boolean) => void;
  setCurrentView: (view: ViewType) => void;
  handleSearchSubmit: (data: TFormData) => void;
  handleViewChange: (view: ViewType) => void;
  handleSortChange: (value: string) => void;
  handleExactMatchChange: (checked: boolean) => void;
}

export const useCourtSearchPage = <
  TSearchType extends string,
  TFormData extends {
    searchQuery: string;
    exactMatchOnly: boolean;
    filters: unknown;
  }
>(
  config: UseCourtSearchPageConfig<TSearchType>,
  normalizeFilters: (filters: unknown) => Record<string, unknown>
): UseCourtSearchPageReturn<TSearchType, TFormData> => {
  const {
    queryPrefixMap,
    defaultSearchType,
    documentTitle,
    scrollElementId,
    searchQuery: searchQueryFromHook,
    sortOption,
    setSortOption,
    triggerCombinedSearch,
    currentSearchQuery: initialCurrentSearchQuery,
    hasResults,
    isSearching,
    refetchOnSortChange = true,
  } = config;

  const { search } = useLocation();
  const queryParams = useQueryParams(search);
  const query = queryParams.get('query') || '';
  const exactMatchParam = queryParams.get('exact_match');
  const initialExactMatch = exactMatchParam !== 'false';
  const initialSearchType = getSearchTypeFromQuery(
    query,
    queryPrefixMap,
    defaultSearchType
  );

  const [currentSearchQuery, setCurrentSearchQuery] = useState(
    initialCurrentSearchQuery || query
  );
  const [exactMatch, setExactMatch] = useState(initialExactMatch);
  const [currentSearchType, setCurrentSearchType] =
    useState<TSearchType>(initialSearchType);
  const [shouldScrollAfterLoading, setShouldScrollAfterLoading] =
    useState(false);
  const [currentView, setCurrentView] = useState<ViewType>('grid');

  // Set document title
  useEffect(() => {
    document.title = documentTitle;
  }, [documentTitle]);

  // Sync query from URL
  useEffect(() => {
    setCurrentSearchQuery(query);
    setCurrentSearchType(
      getSearchTypeFromQuery(query, queryPrefixMap, defaultSearchType)
    );
  }, [query, queryPrefixMap, defaultSearchType]);

  // Sync exactMatch from URL
  useEffect(() => {
    setExactMatch(initialExactMatch);
  }, [initialExactMatch]);

  // Handle scroll after loading
  useEffect(() => {
    if (shouldScrollAfterLoading && !isSearching && hasResults) {
      scrollToElement(scrollElementId);
      setShouldScrollAfterLoading(false);
    }
  }, [shouldScrollAfterLoading, isSearching, hasResults, scrollElementId]);

  const handleSearchSubmit = (data: TFormData) => {
    const searchQueryText = data.searchQuery.trim();
    setCurrentSearchQuery(searchQueryText);
    setExactMatch(data.exactMatchOnly);

    const apiFilters = normalizeFilters(data.filters);
    apiFilters.exact_match = data.exactMatchOnly;

    updateUrlParams({
      query: searchQueryText || null,
      exact_match: data.exactMatchOnly ? 'true' : null,
    });

    setShouldScrollAfterLoading(true);
    triggerCombinedSearch(searchQueryText, apiFilters);
  };

  const buildAppliedFiltersDisplay = (): AppliedFilter[] => {
    const items: AppliedFilter[] = [];
    items.push({ label: 'Courts', value: 'All' });

    if (searchQueryFromHook && searchQueryFromHook.trim()) {
      items.push({ label: 'Query', value: searchQueryFromHook.trim() });
    }

    return items;
  };

  const handleViewChange = (view: ViewType) => {
    setCurrentView(view);
  };

  const handleSortChange = (value: string) => {
    setSortOption(value);
    if (!refetchOnSortChange) {
      return;
    }
    if (searchQueryFromHook) {
      setShouldScrollAfterLoading(true);
      triggerCombinedSearch(currentSearchQuery, {
        order_by: value,
        exact_match: exactMatch,
      });
    }
  };

  const handleExactMatchChange = (checked: boolean) => {
    setExactMatch(checked);
    if (currentSearchQuery.trim()) {
      setShouldScrollAfterLoading(true);
      triggerCombinedSearch(currentSearchQuery, {
        exact_match: checked,
        order_by: sortOption,
      });
    }
  };

  const appliedFilters = buildAppliedFiltersDisplay();

  return {
    currentSearchQuery,
    exactMatch,
    currentSearchType,
    shouldScrollAfterLoading,
    currentView,
    appliedFilters,
    setCurrentSearchQuery,
    setExactMatch,
    setCurrentSearchType,
    setShouldScrollAfterLoading,
    setCurrentView,
    handleSearchSubmit,
    handleViewChange,
    handleSortChange,
    handleExactMatchChange,
  };
};
